import Error from "../utils/Error.js";
import global from "../global.js";

import fetch_retry_factory from "fetch-retry";
import StateMachine from "statemachine";

const fetch_retry = fetch_retry_factory(fetch);

const log = global.log.getLogger("f_mtac.Network");
log.setLevel(log.levels.WARN);

const Network = function (server) {
    var sm = new StateMachine({
        name: "server_connection",
        shared_data: {},
        default_state: "undefined",
        states: {
            undefined: {
                in: () => {},
                out: () => {},
                transit: {
                    offline: {
                        state: "offline",
                        handler: async () => {
                            return true;
                        }
                    },
                    online: {
                        state: "online",
                        handler: async () => {
                            return true;
                        }
                    }
                },
                schedule: {}
            },
            offline: {
                in: () => {
                    log.debug("Network.sm publish 'network.offline'");
                    global.event_bus.publish("network.offline");
                },
                out: () => {},
                transit: {
                    online: {
                        state: "online",
                        handler: () => {
                            return Promise.resolve(true);
                        }
                    }
                },
                schedule: {
                    ping_timer: {
                        handler: (schedule_data) => {
                            log.info("Network.sm polling server (offline)", schedule_data);
                            this.ping().catch(() => log.info("Network.sm ping failed (offline)"));
                        },
                        interval: 10 * 1e3, // 10 * 1s = 10 seconds
                        repeat: Infinity
                    }
                }
            },
            online: {
                in: () => {
                    log.debug("Network.sm publish 'network.online'");
                    global.event_bus.publish("network.online");
                },
                out: () => {},
                transit: {
                    offline: {
                        state: "offline",
                        handler: () => {
                            return Promise.resolve(true);
                        }
                    }
                },
                schedule: {
                    ping_timer: {
                        handler: (schedule_data) => {
                            log.info("Network.sm polling server (online)", schedule_data);
                            this.ping().catch(() => log.info("ping failed (online)"));
                        },
                        interval: 600e3, //10 * 60 * 1e3, // 10 * 60 * 1s = 10 min
                        //interval: 10e3, // for DEBUG 
                        repeat: Infinity
                    }
                }
            }
        }
    });
    window.addEventListener("offline", () => {
        sm.transition.offline({});
    });
    window.addEventListener("online", () => {
        sm.transition.online({});
    });

    Object.defineProperty(this, "state", {
        get: () => sm.state.name,
        set: () => {}
    });

    this.cut = () => {
        sm.transition.offline({});
    };

    this.ping = () => server.mtac_ping();

    this.admin_ping = () => server.mtac_admin_ping();

    const response_match_content_type = (response, required_content_type) => {
        var content_type = response.headers.get("content-type");
        if (content_type === null) {
            return false;
        }
        return content_type.indexOf(required_content_type) !== -1;
    };

    this.common_fetch_error = async (response) => {
        var is_likely_json = response_match_content_type(response, "application/json");
        var {status} = response;
        if (status === 401 && !is_likely_json) {
            // 401 may not have json payloads for now, in some cases
            return {
                status: 401,
                code: Error.codes.EACCES,
                message: "unauthorized"
            };
        }
        if (status === 413) {
            // 413 Request entity too large
            return {
                status: 413,
                code: Error.codes.EOVERFLOW,
                message: "invalid request"
            };
        }
        try {
            var {code, message} = (await response.json()).error;
        } catch (error) {
            throw new Error.build.general({
                message: `failed to parse error response ${status} error`,
                data: {response}
            });
        }
        return {status, code, message};
    };

    this.common_fetch_error_handler = ({status, code, message}) => {
        log.debug("common_fetch_error_handler ", {status, code, message});
        switch (status) {
            case 400:
                throw new Error.build.invalid({
                    message: `server can't process request for some reason (${message})`,
                    data: {status, code, message}
                });
            case 401:
                throw new Error.build.auth({
                    message: `no rights (${message})`,
                    data: {status, code, message}
                });
            case 403:
                throw new Error.build.access({
                    message: `failed to login (${message})`,
                    data: {status, code, message}
                });
            case 413:
                throw new Error.build.invalid({
                    message: `invalid request (${message})`,
                    data: {status, code, message}
                });
            case 500:
                throw new Error.build.fatal({
                    message: `internal server failure (${message})`,
                    data: {status, code, message}
                });
            default:
                throw new Error.build.general({
                    message: `unexpected response status ${message} error`,
                    data: {status, code, message}
                });
        }
    };

    this.fetch_set_multipart_data = (options, data) => {
        var fd = new FormData();
        for (let key in data) {
            let value = data[key];
            if (value === undefined) {
                continue;
            }
            fd.append(key, value);
        }
        options.body = fd;
    };

    const fetch_set_query_string_data = (url, options, data) => {
        for (let key in data) {
            let value = data[key];
            if (value === undefined) {
                continue;
            }
            url.searchParams.append(key, value);
        }
    };

    const fetch_set_www_form_urlencoded_data = (options, data) => {
        var usp = new URLSearchParams();
        for (let key in data) {
            let value = data[key];
            if (value === undefined) {
                continue;
            }
            usp.append(key, value);
        }
        options.body = usp;
        options.headers = {
            "Content-Type": "application/x-www-form-urlencoded"
        };
    };

    this.fetch_set_json_data = (options, data) => {
        options.body = JSON.stringify(data);
        options.headers = {
            "Content-Type": "application/json"
        };
    };

    this.fetch_set_data = (url, options, data) => {
        if (options.method === "GET" || options.method === "DELETE") {
            fetch_set_query_string_data(url, options, data);
        } else {
            fetch_set_www_form_urlencoded_data(options, data);
        }
    };

    this.fetch_request = (url, method, data, multipart, json, options) => {
        options = {method, ...options};
        if (data !== undefined) {
            if (multipart === true) {
                this.fetch_set_multipart_data(options, data);
            } else if (json === true) {
                this.fetch_set_json_data(options, data);
            } else {
                this.fetch_set_data(url, options, data);
            }        }
        return {url, options};
    };

    this.fetch = (url, options) => {
        if ("timeout" in options) {
            const controller = new AbortController();
            options.signal = controller.signal;
            options.timeout_id = window.setTimeout(() => controller.abort(), options.timeout);
        }
        return Promise.resolve()
            .then(() => {
                if (sm.state.name === "offline" || sm.state.name === "undefined" || options.retry === false) {
                    log.debug("Network.fetch use fetch", sm.state);
                    return fetch(url, options);
                }
                log.debug("Network.fetch use fetch_retry", sm.state);
                return fetch_retry(
                    url,
                    Object.assign(options, {
                        retries: 5,
                        retryDelay: 500
                    })
                );
            })
            .then((response) => {
                log.debug("response", response);
                if ("timeout_id" in options) {
                    window.clearTimeout(options.timeout_id);
                }
                if (sm.state.name === "offline" || sm.state.name === "undefined") {
                    sm.transition.online({});
                }
                return response;
            })
            .catch((error) => {
                if ("timeout_id" in options) {
                    window.clearTimeout(options.timeout_id);
                }

                // AGPOC-9068 : implements cancel media uploading operation
                log.debug("options signal ", options.signal);
                if (options.signal !== undefined && options.signal.aborted === true) {
                    throw new Error.build.canceled({
                        message: "upload file aborted",
                        data: {error}
                    });
                }

                if (sm.state.name === "online" || sm.state.name === "undefined") {
                    sm.transition.offline({});
                }

                throw new Error.build.netdown({
                    message: "network is down",
                    data: {error}
                });
            });
    };
};

export default Network;
